import { ref, toRefs } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import {
  type MediaReleaseAdhocRecipient,
  Contact,
  MediaRelease,
  MediaReleaseRecipient,
} from "shared/resources";

export interface MediaReleaseRecipientOptions {
  mediaRelease: MediaRelease;
}

export function useMediaReleaseRecipient(
  options: MediaReleaseRecipientOptions
) {
  const { mediaRelease } = toRefs(options);

  const savingRecipient = ref(false);
  const savingRecipients = ref(false);

  function getPolymorphicType(
    contact: Contact | MediaReleaseAdhocRecipient
  ): string | undefined {
    return {
      influencers: "Influencer",
      outlets: "Outlet",
      private_influencers: "PrivateInfluencer",
      media_release_adhoc_recipients: "MediaReleaseAdhocRecipient",
    }[contact.resourceIdentifier.type];
  }

  async function saveNewRecipient(contact: Contact) {
    const contactType = getPolymorphicType(contact);

    savingRecipient.value = true;

    const recipient = new MediaReleaseRecipient({
      mediaReleaseId: mediaRelease.value.id,
      contactId: contact.id,
      contactType,
    });

    try {
      await recipient.save();
      recipient.contact = contact;
    } catch (error) {
      ErrorDialog(
        getLocaleText("media_release_step_recipients.recipient_list_add_error"),
        error
      );
    } finally {
      savingRecipient.value = false;
    }
  }

  async function saveNewRecipientsForMediaRelease(documentIds: string[]) {
    try {
      savingRecipients.value = true;

      const query = {
        bool: {
          must: [
            {
              terms: {
                document_id: documentIds,
              },
            },
          ],
        },
      };

      const contacts = await Contact.where({
        query: JSON.stringify(query),
      })
        .page(1)
        .per(999)
        .all();

      const mediaReleaseWithRecipients = await MediaRelease.includes([
        { mediaReleaseRecipients: "contact" },
      ]).find(mediaRelease.value.id!);

      const mediaReleaseRecipients: MediaReleaseRecipient[] = [];

      contacts.data.forEach((contact) => {
        const contactType = getPolymorphicType(contact);

        if (
          mediaReleaseWithRecipients.data.mediaReleaseRecipients.some(
            (mediaReleaseRecipient) =>
              mediaReleaseRecipient.contactType === contactType &&
              Number(mediaReleaseRecipient.contactId) === Number(contact.id)
          )
        ) {
          return;
        }

        mediaReleaseRecipients.push(
          new MediaReleaseRecipient({
            contactId: contact.id,
            contactType,
            mediaReleaseId: mediaRelease.value.id,
          })
        );
      });

      mediaReleaseWithRecipients.data.mediaReleaseRecipients.push(
        ...mediaReleaseRecipients
      );

      await mediaReleaseWithRecipients.data.saveResource({
        with: ["mediaReleaseRecipients"],
      });

      return mediaReleaseWithRecipients;
    } catch (error) {
      ErrorDialog(
        getLocaleText("media_release_step_recipients.recipient_list_add_error"),
        error
      );

      return null;
    } finally {
      savingRecipients.value = false;
    }
  }

  return {
    getPolymorphicType,
    saveNewRecipient,
    saveNewRecipientsForMediaRelease,
    savingRecipient,
    savingRecipients,
  };
}

export default useMediaReleaseRecipient;
